import React, { useCallback, useContext, useMemo } from 'react'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { SaveToDocumentConnectorModal } from '../../../apps/professional-app/fa/MeetingSummary/Header/SaveToDocumentConnectorModal'

import { selectDocument, selectDocumentContact, setDocumentContact } from '../../../store/processDocument/document'
import { setDocumentSummarySyncStatus } from '../../../store/processDocument/documentSummary'
import { aiAgentResultAnchor } from '../../../store/documentConnectors/documentConnectors'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'

import { DocumentExportType } from '../../../components/DocumentConnector/models'
import { useExportToPDF } from '../../../components/PDFExportButton/hooks'

import { type IntegrationServiceId } from '../../../common/integrations'
import { useContactLinkedFoldersByServiceId } from '../../../common/useContactLinkedFolder'
import { formatDateYYYYMMDD } from '../../../common/dateUtils'

import { AIAgentType } from '../../../models/AIAgent'
import { type User } from '../../../models/User'

import { useDocumentSummaryAiAgentResult } from '../hooks'
import { DocumentSummaryContentRefContext } from '../DocumentSummary/DocumentSummaryContentRefContext'

interface DocumentSummaryDocumentConnectorExportModalProps {
  serviceId: IntegrationServiceId
  onHide: () => void
}

export const DocumentSummaryDocumentConnectorExportModal: React.FC<DocumentSummaryDocumentConnectorExportModalProps> = ({ serviceId, onHide }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)
  const contact = useAppSelector(selectDocumentContact)

  const documentSummaryContentRef = useContext(DocumentSummaryContentRefContext)
  const { generatePDFBlob } = useExportToPDF(documentSummaryContentRef)

  const { aiAgentResult } = useDocumentSummaryAiAgentResult()
  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const serviceIdsForContactLinkedFolders = useMemo(() => [serviceId], [serviceId])

  const { folderIdsByServiceId, isLoading: linkedFoldersLoading } = useContactLinkedFoldersByServiceId(serviceIdsForContactLinkedFolders, document?.contactId)

  const handleSaved = useCallback(async () => {
    if (!document) {
      return
    }

    const result = await triggerGetAiAgentResult({ accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: document.id }, false).unwrap()
    if (result) {
      dispatch(setDocumentSummarySyncStatus(result.syncStatus))
    }
  }, [accountId, dispatch, document, triggerGetAiAgentResult])

  const handleContactUpdated = (user: User) => {
    dispatch(setDocumentContact(user))
  }

  const anchor = aiAgentResult ? aiAgentResultAnchor(aiAgentResult.id, document?.contactId) : null

  const contactName = contact ? getUserDisplayName(contact) : ''
  const summaryDate = aiAgentResult?.updatedAt ?? document?.updatedAt
  const dateStr = summaryDate ? formatDateYYYYMMDD(summaryDate) : ''
  const defaultFileName = `Document Summary${contactName ? ` - ${contactName}` : ''}${dateStr ? `_${dateStr}` : ''}.pdf`

  return (
    <SaveToDocumentConnectorModal
      serviceId={serviceId}
      show={true}
      onHide={onHide}
      generatePDFBlob={generatePDFBlob}
      defaultFileName={defaultFileName}
      initialFolderIds={folderIdsByServiceId[serviceId]}
      initialFoldersReady={!linkedFoldersLoading}
      anchor={anchor}
      saveSyncStatus={anchor != null}
      onSaved={handleSaved}
      showSuccessSidebar={false}
      contact={contact}
      onContactUpdated={handleContactUpdated}
      exportType={DocumentExportType.DOCUMENT_SUMMARY}
    />
  )
}
